
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Technique } from '@/types/technique';
import { supabase } from '@/integrations/supabase/client';
import { 
  likeTechnique,
  unlikeTechnique,
  hideTechnique,
  unhideTechnique
} from '@/services';



interface TechniqueLikesProviderProps {
  techniques: Technique[];
  children: (props: {
    likedTechniqueIds: string[];
    hiddenTechniqueIds: string[];
    techniqueLikeCounts: Record<string, number>;
    handleLikeToggle: (id: string) => Promise<void>;
    handleHideToggle: (id: string) => Promise<void>;
  }) => React.ReactNode;
  onRefetch: () => void;
}

export const TechniqueLikesProvider: React.FC<TechniqueLikesProviderProps> = ({
  techniques,
  children,
  onRefetch
}) => {
  const [likedTechniqueIds, setLikedTechniqueIds] = useState<string[]>([]);
  const [hiddenTechniqueIds, setHiddenTechniqueIds] = useState<string[]>([]);
  const [techniqueLikeCounts, setTechniqueLikeCounts] = useState<Record<string, number>>({});
  
  // Create stable dependencies to avoid unnecessary re-renders
  const techniqueIds = useMemo(() => techniques.map(t => t.id).sort(), [techniques]);

  const techniquesStable = useMemo(() => {
    return {
      length: techniques.length,
      idsHash: techniqueIds.join(','),
      ids: techniqueIds,
      techniques,
    };
  }, [techniques, techniqueIds]);
  
  // Memoize user-created IDs to avoid recalculation
  const userCreatedStable = useMemo(() => {
    const userCreatedIds = techniquesStable.techniques
      .filter((t: Technique) => t.is_user_created)
      .map((t: Technique) => t.id)
      .sort();
    return {
      length: userCreatedIds.length,
      ids: userCreatedIds,
      idsHash: userCreatedIds.join(','),
    };
  }, [techniquesStable.techniques]);
  
  // Batch fetch liked statuses and counts in one efficient query
  useEffect(() => {
    if (!techniquesStable.length) return;
    
    const fetchTechniqueStatuses = async () => {
      try {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData?.user) return;
        
        // Batch get all like counts from the view
        const { data: likeCounts, error: likeCountsError } = await supabase
          .from('technique_like_counts')
          .select('technique_id, like_count')
          .in('technique_id', techniquesStable.ids);
          
        if (likeCountsError) {
          console.error('Error fetching like counts:', likeCountsError);
        } else {
          const countsMap: Record<string, number> = {};
          likeCounts?.forEach((item: { technique_id: string; like_count: number }) => {
            countsMap[item.technique_id] = item.like_count || 0;
          });
          setTechniqueLikeCounts(countsMap);
        }
        
        // Batch get user likes
        const { data: userLikes, error: userLikesError } = await supabase
          .from('technique_likes')
          .select('technique_id')
          .eq('user_id', userData.user.id)
          .in('technique_id', techniquesStable.ids);
          
        if (userLikesError) {
          console.error('Error fetching user likes:', userLikesError);
        } else {
          setLikedTechniqueIds(userLikes?.map((like: { technique_id: string }) => like.technique_id) || []);
        }
        
        // Only get hidden statuses for user-created techniques
        if (userCreatedStable.length > 0) {
          const { data: hiddenItems, error: hiddenError } = await supabase
            .from('hidden_techniques')
            .select('technique_id')
            .eq('user_id', userData.user.id)
            .in('technique_id', userCreatedStable.ids);
            
          if (hiddenError) {
            console.error('Error fetching hidden techniques:', hiddenError);
          } else {
            setHiddenTechniqueIds(hiddenItems?.map((item: { technique_id: string }) => item.technique_id) || []);
          }
        }
      } catch (error) {
        console.error('Error in batch fetching technique statuses:', error);
      }
    };
    
    fetchTechniqueStatuses();
  }, [
    techniquesStable.idsHash,
    techniquesStable.ids,
    techniquesStable.length,
    userCreatedStable.idsHash,
    userCreatedStable.ids,
    userCreatedStable.length,
  ]);

  const handleLikeToggle = useCallback(async (id: string) => {
    try {
      if (likedTechniqueIds.includes(id)) {
        await unlikeTechnique(id);
        setLikedTechniqueIds((prev: string[]) => prev.filter((techniqueId: string) => techniqueId !== id));
        setTechniqueLikeCounts((prev: Record<string, number>) => ({
          ...prev,
          [id]: Math.max(0, (prev[id] || 0) - 1)
        }));
      } else {
        await likeTechnique(id);
        setLikedTechniqueIds((prev: string[]) => [...prev, id]);
        setTechniqueLikeCounts((prev: Record<string, number>) => ({
          ...prev,
          [id]: (prev[id] || 0) + 1
        }));
      }
      // Refetch to update like counts
      onRefetch();
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  }, [likedTechniqueIds, onRefetch]);

  const handleHideToggle = useCallback(async (id: string) => {
    try {
      if (hiddenTechniqueIds.includes(id)) {
        await unhideTechnique(id);
        setHiddenTechniqueIds((prev: string[]) => prev.filter((techniqueId: string) => techniqueId !== id));
      } else {
        await hideTechnique(id);
        setHiddenTechniqueIds((prev: string[]) => [...prev, id]);
        // Remove from the current display
        onRefetch();
      }
    } catch (error) {
      console.error('Error toggling hide:', error);
    }
  }, [hiddenTechniqueIds, onRefetch]);

  // Memoize the props object to reduce re-renders in children
  const providerValue = useMemo(() => {
    return {
      likedTechniqueIds,
      hiddenTechniqueIds,
      techniqueLikeCounts,
      handleLikeToggle,
      handleHideToggle
    };
  }, [
    likedTechniqueIds,
    hiddenTechniqueIds,
    techniqueLikeCounts,
    handleLikeToggle,
    handleHideToggle
  ]);

  return <>{children(providerValue)}</>;
};
